/*
  <product-tabs> — PDP tabbed feature section.
  - Switches top-level tabs (All Features / What's In The Box / Software Feature Access / FAQ).
  - Switches sub-tabs inside All Features (bucket groups for Prosumer/Professional tools).
  - Opens a specific tab from anywhere on the PDP:
      • a link with [data-open-tab="all-features"] (paired with href="#product-tabs-anchor"), or
      • a URL hash like #product-tabs=software-feature-access
  - First tab + first sub-tab are selected on load; first drawer in each panel opens by default.
  - Accessibility: tabs/sub-tabs are wired as WAI-ARIA tablists — aria-controls / aria-labelledby
    link each tab to its panel, roving tabindex keeps one tab in the Tab order, and Left/Right/
    Home/End move between tabs (automatic activation).
*/
if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId || '';
        this.tabs = Array.from(this.querySelectorAll('.pdp-tabs__tab'));
        this.panels = Array.from(this.querySelectorAll('.pdp-tabs__panel'));
        this.subTabs = Array.from(this.querySelectorAll('.pdp-subtabs__tab'));
        this.tablist = this.querySelector('.pdp-tabs__nav');

        // Link each tab to its panel (aria-controls / aria-labelledby) and give panels an id.
        this.tabs.forEach((tab) => {
          const panel = this.panels.find((p) => p.dataset.tab === tab.dataset.tab);
          if (!panel) return;
          if (!panel.id) panel.id = `pdp-panel-${tab.dataset.tab}-${this.sectionId}`;
          tab.setAttribute('aria-controls', panel.id);
          if (tab.id) panel.setAttribute('aria-labelledby', tab.id);
        });

        this.tabs.forEach((t) => t.addEventListener('click', () => this.selectTab(t.dataset.tab)));
        this.subTabs.forEach((t) => t.addEventListener('click', () => this.selectSubTab(t)));

        // Keyboard: arrow / Home / End roving across the top-level tablist.
        if (this.tablist) {
          if (!this.tablist.getAttribute('role')) this.tablist.setAttribute('role', 'tablist');
          this.tablist.addEventListener('keydown', (e) => this.onTablistKeydown(e, this.tabs, (t) => this.selectTab(t.dataset.tab)));
        }

        // Wire each sub-tablist (aria-controls + keyboard) independently.
        this.querySelectorAll('.pdp-subtabs__nav').forEach((nav) => {
          const scope = nav.closest('.pdp-subtabs');
          if (!scope) return;
          const subTabs = Array.from(scope.querySelectorAll('.pdp-subtabs__tab'));
          const subPanels = Array.from(scope.querySelectorAll('.pdp-subtabs__panel'));
          subTabs.forEach((tab, i) => {
            const panel = subPanels.find((p) => p.dataset.subtab === tab.dataset.subtab);
            if (panel) {
              if (!panel.id) panel.id = `pdp-subpanel-${tab.dataset.subtab}-${this.sectionId}-${i}`;
              tab.setAttribute('aria-controls', panel.id);
              if (!tab.id) tab.id = `pdp-subtab-${tab.dataset.subtab}-${this.sectionId}-${i}`;
              panel.setAttribute('aria-labelledby', tab.id);
            }
            tab.setAttribute('tabindex', tab.getAttribute('aria-selected') === 'true' ? '0' : '-1');
          });
          if (!nav.getAttribute('role')) nav.setAttribute('role', 'tablist');
          nav.addEventListener('keydown', (e) => this.onTablistKeydown(e, subTabs, (t) => this.selectSubTab(t)));
        });

        // Feature / FAQ drawers: accordion on mobile, always-expanded on desktop (>=750px).
        this.featureDrawers = Array.from(
          this.querySelectorAll(
            '.pdp-tabs__panel[data-tab="all-features"] details.pdp-feature, .pdp-tabs__panel[data-tab="software-feature-access"] details.pdp-feature, .pdp-tabs__panel[data-tab="faq"] details.pdp-feature'
          )
        );
        this.featureDrawers.forEach((d) => {
          d.dataset.mobileOpen = d.open ? 'true' : 'false';
        });
        this.desktopMql = window.matchMedia('(min-width: 750px)');
        this.syncFeatureDrawers = () => {
          const desktop = this.desktopMql.matches;
          this.featureDrawers.forEach((d) => {
            d.open = desktop ? true : d.dataset.mobileOpen === 'true';
          });
        };
        this.syncFeatureDrawers();
        this.desktopMql.addEventListener('change', this.syncFeatureDrawers);

        // Normalize initial state (handles the case where the first tab is not All Features).
        const initial = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || this.tabs[0];
        if (initial) this.selectTab(initial.dataset.tab);

        // Anchor links elsewhere on the PDP (feature list, warranty list, etc.).
        document.querySelectorAll('[data-open-tab]').forEach((link) => {
          link.addEventListener('click', () => {
            const target = link.dataset.openTab;
            // Let the native href="#product-tabs-anchor" scroll fire first, then open the tab.
            window.setTimeout(() => this.selectTab(target, true), 0);
          });
        });

        this.handleHash();
        window.addEventListener('hashchange', () => this.handleHash());
      }

      // Shared roving-tabindex keyboard handler for a tablist (top-level or sub).
      onTablistKeydown(e, tabs, activate) {
        const current = tabs.indexOf(document.activeElement);
        if (current === -1) return;
        let next = null;
        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = (current + 1) % tabs.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = (current - 1 + tabs.length) % tabs.length;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = tabs.length - 1;
            break;
          default:
            return;
        }
        e.preventDefault();
        const target = tabs[next];
        activate(target);
        target.focus();
      }

      selectTab(name, scroll) {
        if (!name) return;
        let found = false;
        this.tabs.forEach((t) => {
          const on = t.dataset.tab === name;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
          if (on) found = true;
        });
        if (!found) return;
        this.panels.forEach((p) => {
          p.hidden = p.dataset.tab !== name;
        });
        if (scroll) this.scrollToAnchor();
      }

      selectSubTab(btn) {
        const name = btn.dataset.subtab;
        const scope = btn.closest('.pdp-subtabs');
        if (!scope) return;
        scope.querySelectorAll('.pdp-subtabs__tab').forEach((t) => {
          const on = t === btn;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
        });
        scope.querySelectorAll('.pdp-subtabs__panel').forEach((p) => {
          p.hidden = p.dataset.subtab !== name;
        });
      }

      handleHash() {
        const m = window.location.hash.match(/product-tabs=([\w-]+)/);
        if (m) this.selectTab(m[1], true);
      }

      scrollToAnchor() {
        const a = document.getElementById('product-tabs-anchor');
        if (a) a.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  );
}
